import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { usersApi } from "@/api/users";
import type { DocumentCategory, IngestionStatus } from "@/types/api";

/**
 * Document filter values bound to the existing /api/documents query
 * params. All fields optional; an absent field means "no constraint".
 *
 * The backend's `category` is a single nullable enum (not a list), so the
 * UI exposes a single-select Category dropdown — deliberately diverging
 * from the v1 spec's "multi-select" wording. Multi-select would require
 * a backend list-binding change that's out of scope for this fix.
 */
export interface DocumentFilters {
  category?: DocumentCategory;
  ingestionStatus?: IngestionStatus;
  uploadedBy?: string;
  dateFrom?: string;
  dateTo?: string;
  tags?: string;
}

export const EMPTY_FILTERS: DocumentFilters = {};

export function countActiveFilters(f: DocumentFilters): number {
  return Object.values(f).filter((v) => v !== undefined && v !== "").length;
}

const CATEGORY_OPTIONS: DocumentCategory[] = [
  "Application", "Appraisal", "BackgroundCheck", "Branding",
  "ClosingDocument", "ComplianceDocument", "Correspondence", "CreditReport",
  "DealSheet", "FinancialStatement", "Form", "Identification",
  "InsuranceCertificate", "InvestorReport", "Invoice", "LegalDocument",
  "Manual", "Other", "Photo", "Policy",
  "ServiceReport", "Signature", "StandardForms", "TaxDocument",
  "Template", "TitleWork", "VendorAgreement",
];

// Backend supports five ingestion states but only three are user-facing
// per the spec (Ready / Processing / Failed). "Ready" maps to "Completed"
// — both ingestion-completed and pre-DMS legacy "Ingested" roll up to it.
const INGESTION_LABELS: Array<{ label: string; value: IngestionStatus }> = [
  { label: "Ready", value: "Completed" },
  { label: "Processing", value: "Pending" },
  { label: "Failed", value: "Failed" },
];

interface FilterPanelProps {
  open: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  initial: DocumentFilters;
  onApply: (next: DocumentFilters) => void;
  onClear: () => void;
}

export function DocumentFilterPanel({
  open,
  anchorRef,
  onClose,
  initial,
  onApply,
  onClear,
}: FilterPanelProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState<DocumentFilters>(initial);

  // Re-sync the draft each time the popover opens so the user's URL
  // state always seeds the form, even if they navigated since last open.
  useEffect(() => {
    if (open) setDraft(initial);
  }, [open, initial]);

  // Close on outside click + Escape — match Modal's affordances since
  // there's no Popover primitive yet.
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open, onClose, anchorRef]);

  // Shallow user list for the uploader typeahead. Capped at 200 — a fully
  // server-side typeahead is out of scope; client-side filtering against
  // a paged list is enough for the workspaces this serves today.
  const usersQuery = useQuery({
    queryKey: ["users", "filter-panel"],
    queryFn: () => usersApi.list({ pageSize: 200 }),
    enabled: open,
    staleTime: 60_000,
  });

  const userOptions = useMemo(
    () => usersQuery.data?.data ?? [],
    [usersQuery.data],
  );

  if (!open) return null;

  const set = <K extends keyof DocumentFilters>(key: K, value: DocumentFilters[K]) => {
    setDraft((prev) => {
      const next = { ...prev };
      if (value === undefined || value === "") {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  };

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Filter documents"
      // Anchored under the button. Tailwind safelist already covers
      // these utilities since they're used elsewhere in the app.
      className="absolute right-0 top-full mt-space-2 z-40 w-[360px] max-w-[95vw] bg-white-pure border border-border-light rounded-md shadow-lg"
    >
      <div className="flex items-center justify-between px-space-3 py-space-2 border-b border-border-light">
        <h3 className="font-display font-bold text-h4 text-onyx m-0">Filters</h3>
        <button
          type="button"
          onClick={onClose}
          className="p-space-1 -m-space-1 rounded-full text-gray-mid hover:text-onyx hover:bg-warm-white"
          aria-label="Close filters"
        >
          <X size={14} aria-hidden />
        </button>
      </div>

      <div className="p-space-3 flex flex-col gap-space-3 max-h-[60vh] overflow-y-auto">
        <FormField label="Category" htmlFor="filter-category">
          <Select
            id="filter-category"
            value={draft.category ?? ""}
            onChange={(e) => set("category", (e.target.value || undefined) as DocumentCategory | undefined)}
          >
            <option value="">All categories</option>
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </Select>
        </FormField>

        <FormField label="Ingestion status" htmlFor="filter-ingestion">
          <Select
            id="filter-ingestion"
            value={draft.ingestionStatus ?? ""}
            onChange={(e) => set("ingestionStatus", (e.target.value || undefined) as IngestionStatus | undefined)}
          >
            <option value="">Any status</option>
            {INGESTION_LABELS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Select>
        </FormField>

        <FormField label="Uploader" htmlFor="filter-uploader">
          <Select
            id="filter-uploader"
            value={draft.uploadedBy ?? ""}
            onChange={(e) => set("uploadedBy", e.target.value || undefined)}
            disabled={usersQuery.isLoading}
          >
            <option value="">Any uploader</option>
            {userOptions.map((u) => (
              <option key={u.id} value={u.id}>{u.displayName}</option>
            ))}
          </Select>
        </FormField>

        <div className="grid grid-cols-2 gap-space-2">
          <FormField label="Uploaded from" htmlFor="filter-date-from">
            <Input
              id="filter-date-from"
              type="date"
              value={draft.dateFrom ?? ""}
              onChange={(e) => set("dateFrom", e.target.value || undefined)}
            />
          </FormField>
          <FormField label="Uploaded to" htmlFor="filter-date-to">
            <Input
              id="filter-date-to"
              type="date"
              value={draft.dateTo ?? ""}
              onChange={(e) => set("dateTo", e.target.value || undefined)}
            />
          </FormField>
        </div>

        <FormField label="Tags" htmlFor="filter-tags">
          <Input
            id="filter-tags"
            placeholder="e.g. finance"
            value={draft.tags ?? ""}
            onChange={(e) => set("tags", e.target.value || undefined)}
          />
        </FormField>
      </div>

      <div className="flex items-center justify-between gap-space-2 px-space-3 py-space-2 border-t border-border-light bg-warm-white rounded-b-md">
        <Button
          variant="tertiary"
          size="sm"
          onClick={() => {
            setDraft({});
            onClear();
          }}
        >
          Clear filters
        </Button>
        <Button size="sm" onClick={() => onApply(draft)}>
          Apply
        </Button>
      </div>
    </div>
  );
}
